import uniqueId from 'lodash/uniqueId';
import { RESOURCE_FIELD_NAMES, TRIGGER_METADATA } from 'modules/workflows/constants';
import { startUtils } from 'modules/workflows/utils';
import {
  booleanTypeComparators,
  brandAffinityComparators,
  BRAND_AFFINITY_FIELD,
  campaignLabelComparators,
  dateTypeComparators,
  defaultComparators,
  DEFAULT_SEARCH_COMBINATOR,
  DEFAULT_SEARCH_DATATYPE,
  listTypeComparators,
  messageTypeComparators,
  numericTypeComparators,
  stringTypeComparators,
} from 'modules/journey/components/FilterInputsList/FilterInputsList.constants';

// get the relevant comparators for the field
export const getComparators = (searchField) => {
  if (!searchField) return [];

  if (searchField.field === BRAND_AFFINITY_FIELD) {
    return brandAffinityComparators;
  }
  switch (searchField.fieldType) {
    case 'boolean':
      return booleanTypeComparators;

    case 'string':
      return stringTypeComparators;

    case 'date':
      return dateTypeComparators;

    default:
      switch (searchField.field) {
        case 'emailListIds':
        case 'emailListId':
        case 'templateId':
        case 'canonicalUrlId':
          return listTypeComparators;
        case 'unsubscribedMessageTypeIds':
          return messageTypeComparators;
        case 'subscribedMessageTypeIds':
          return messageTypeComparators;
        case 'campaignLabelId':
          return campaignLabelComparators;
        case BRAND_AFFINITY_FIELD:
          return brandAffinityComparators;
        default:
          if (searchField.castType === 'EpochSecs' || searchField.castType === 'EpochMillis') {
            return dateTypeComparators;
          }
          if (searchField.fieldType === 'double' || searchField.fieldType === 'long') {
            return numericTypeComparators;
          }
          return defaultComparators;
      }
  }
};

/**
 * Adds an id to each query
 * This is needed so that react knows to render the correct filter when the size
 * of the query list changes (eg: when removing a filter)
 */
export const parseSearchQuery = (query = {}) => ({ ...query, id: uniqueId('search-query_') });

export const parseSearchQueries = (queries = []) => queries.map(parseSearchQuery);

/**
 * Return a blank query - for adding query types
 */
export const getQueryStub = (dataType = DEFAULT_SEARCH_DATATYPE) =>
  parseSearchQuery({
    dataType,
    castType: null,
    comparatorType: null,
    dynamicValue: null,
    field: null,
    fieldType: null,
    value: null,
  });

export const getQueriesStub = (dataType = DEFAULT_SEARCH_DATATYPE) => [getQueryStub(dataType)];

export const getInitialSearchQueries = (queries = []) =>
  Array.isArray(queries) && queries.length ? parseSearchQueries(queries) : [];

export const getDefaultSearchCombo = (dataType = DEFAULT_SEARCH_DATATYPE) => ({
  combinator: DEFAULT_SEARCH_COMBINATOR,
  searchQueries: getQueriesStub(dataType),
});

export const getUpdatedSearchCombo = (
  { searchQueries, combinator },
  { nodeData, dataType = DEFAULT_SEARCH_DATATYPE },
) => {
  return {
    ...(nodeData?.searchCombo ?? getDefaultSearchCombo(dataType)),
    ...(combinator ? { combinator } : {}),
    ...(searchQueries ? { searchQueries } : {}),
  };
};

export const getTriggerMetadata = (nodeData /*: StartNodeData */) =>
  TRIGGER_METADATA[nodeData?.nodeClass];

const shouldAddCombo = (nodeData) => {
  const { hasFilter } = getTriggerMetadata(nodeData);
  const { searchQueries } = nodeData?.searchCombo ?? {};
  if (!hasFilter) return false;
  if (!searchQueries?.length) return false;
  return searchQueries[0].field && searchQueries[0].comparatorType;
};

export const parseNodeDataForSaving = (nodeData /*: StartNodeData */) => {
  const hasCombo = shouldAddCombo(nodeData);
  const resourceField = startUtils.getResourceField(nodeData);

  // Unset any of the fields that are not the resource field
  // NOTE: There is a possibility that future triggers will need these fields
  // for other purposes.  If this is the case, this logic should be revised.
  const updatedNodeData = RESOURCE_FIELD_NAMES.reduce(
    (acc, f) => (f === resourceField ? acc : { ...acc, [f]: undefined }),
    nodeData,
  );

  if (!updatedNodeData[resourceField]) {
    updatedNodeData.resource = { name: null };
  }
  if (!hasCombo) {
    updatedNodeData.searchCombo = null;
  }

  return updatedNodeData;
};

export const parseNodeDataForUpdatedTrigger = (
  nodeClass /*: string */,
  initialNodeData /*: StartNodeData */,
) => {
  const initialCombo = initialNodeData?.searchCombo;

  // if selected trigger is same type as currently saved one
  // and contains filter, check whether filter is active or not
  // if trigger is not saved, default filter state to off
  const isSelectedTriggerSaved = nodeClass === initialNodeData?.nodeClass;
  const isSavedFilterActive = !!initialCombo;
  const isInitialComboValid = isSelectedTriggerSaved && isSavedFilterActive;
  const updatedMetadata = TRIGGER_METADATA[nodeClass];
  const queries = isInitialComboValid
    ? initialCombo?.searchQueries
    : getQueriesStub(updatedMetadata.dataType);

  return {
    searchCombo: {
      searchQueries: getInitialSearchQueries(queries),
      combinator: isInitialComboValid ? initialCombo?.combinator : DEFAULT_SEARCH_COMBINATOR,
    },
    nodeClass,
    // clear out previous resources when trigger type changes
    eventName: '',
    resource: null,
    emailListId: null,
    emailCampaignId: null,
  };
};
